import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Avatar,
    Button,
    CircularProgress,
    Alert,
    Chip
} from '@mui/material';
import { PersonAdd, People } from '@mui/icons-material';
import { socialAPI, toggleFollow } from '../utils/api';
import { useNavigate } from 'react-router-dom';

const SuggestedUsers = ({ limit = 5, showTitle = true }) => {
    const [suggestedUsers, setSuggestedUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [followingActions, setFollowingActions] = useState({});
    const navigate = useNavigate();

    const fetchSuggestedUsers = async () => {
        try {
            setLoading(true);
            const response = await socialAPI.getSuggestedUsers(limit);
            setSuggestedUsers(response.data.suggested_users || []);
            setError(null);
        } catch (err) {
            setError('Failed to load suggested users');
            console.error('Error fetching suggested users:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSuggestedUsers();
    }, [limit]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFollow = async (userId) => {
        if (followingActions[userId]) return;

        try {
            setFollowingActions(prev => ({ ...prev, [userId]: true }));
            
            await toggleFollow(userId);
            
            // Remove the user from suggestions after following
            setSuggestedUsers(prev => prev.filter(user => user.id !== userId));
        } catch (err) {
            console.error('Error following user:', err);
        } finally {
            setFollowingActions(prev => ({ ...prev, [userId]: false }));
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    if (suggestedUsers.length === 0) {
        return null;
    }

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                {showTitle && (
                    <Box display="flex" alignItems="center" mb={2}>
                        <People sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" component="h2">
                            Suggested for you
                        </Typography>
                    </Box>
                )}
                
                {suggestedUsers.map((user) => (
                    <Box 
                        key={user.id} 
                        display="flex" 
                        alignItems="center" 
                        py={1}
                        borderBottom={1}
                        borderColor="divider"
                        sx={{ '&:last-child': { borderBottom: 0 } }}
                    >
                        <Avatar 
                            src={user.avatar_url} 
                            alt={user.username}
                            sx={{ 
                                mr: 2, 
                                cursor: 'pointer',
                                width: 40,
                                height: 40
                            }}
                            onClick={() => navigate(`/profile/${user.id}`)}
                        />
                        <Box flex={1}>
                            <Typography 
                                variant="subtitle2" 
                                component="div"
                                sx={{ 
                                    cursor: 'pointer',
                                    fontWeight: 500
                                }}
                                onClick={() => navigate(`/profile/${user.id}`)}
                            >
                                {user.username}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {user.followers_count} followers
                                </Typography>
                                {user.mutual_connections > 0 && (
                                    <Chip 
                                        label={`${user.mutual_connections} mutual`} 
                                        size="small" 
                                        variant="outlined"
                                        color="primary"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                )}
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<PersonAdd />}
                            disabled={followingActions[user.id]}
                            onClick={() => handleFollow(user.id)}
                            sx={{ ml: 1 }}
                        >
                            {followingActions[user.id] ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                'Follow'
                            )}
                        </Button>
                    </Box>
                ))}
            </CardContent>
        </Card>
    );
};

export default SuggestedUsers;